import MattersSearchBar from "@/components/features/matters/MattersSearchBar/MattersSearchBar";
import { MattersTable } from "@/components/features/matters/MattersTable/MattersTable";
import { Button, Slideover, Typography } from "@/components/ui";
import { useSearchObject } from "@/hooks/useSearchObject";
import { type MatterSearchResult } from "@/lib/data/requests";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { cn } from "@/lib/utils/cn";
import { MatterIcon } from "@/lib/utils/iconUtils";
import { useEffect, useMemo, useState } from "react";

interface MatterSelectorFormFieldProps {
  label?: string;
  selectionHint?: string;
  className?: string;
  autoOpenMatterSlideover?: boolean;
  parentSlideoverOpen?: boolean;
  onMatterSelected: (matter: MatterSearchResult) => void;
  selectedMatter?: MatterSearchResult | null;
  slideoverTitle?: string;
  slideoverDescription?: string;
  tableName?: (typeof tanstackTableNames)[keyof typeof tanstackTableNames];
}

export const MatterSelectorFormField = ({
  label,
  selectionHint = "Search all matters to attach this task.",
  className,
  autoOpenMatterSlideover = false,
  parentSlideoverOpen,
  onMatterSelected,
  selectedMatter,
  slideoverTitle,
  slideoverDescription,
  tableName = tanstackTableNames.dashboardCreateTaskMatters,
}: MatterSelectorFormFieldProps) => {
  const [open, setOpen] = useState(false);
  const { searchObject, addSearchObjectValue, removeSearchObjectValue } =
    useSearchObject({
      defaultSearchObject: {},
      itemType: "matter",
    });

  useEffect(() => {
    if (!autoOpenMatterSlideover) return;
    if (parentSlideoverOpen) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [autoOpenMatterSlideover, parentSlideoverOpen]);

  const selectedMatterTitle = useMemo(
    () => selectedMatter?.title?.trim(),
    [selectedMatter?.title],
  );

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <Typography size="sm" weight="medium">
          {label}
        </Typography>
      )}
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setOpen(true);
        }}
        className="h-40 w-full border border-dashed"
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <MatterIcon className="h-5 w-5" />
          <Typography className="text-sm">
            {selectedMatterTitle || "Select a matter"}
          </Typography>
          {!selectedMatterTitle && (
            <Typography variant="subHeading" className="text-muted-foreground">
              {selectionHint}
            </Typography>
          )}
        </div>
      </Button>
      {selectedMatterTitle && (
        <Typography variant="subHeading" className="text-muted-foreground mt-2">
          Selected matter: {selectedMatterTitle}
        </Typography>
      )}
      <Slideover
        title={slideoverTitle ?? "Select matter"}
        description={
          slideoverDescription ?? "Select a matter to attach this task to."
        }
        open={open}
        setOpen={setOpen}
        size="large"
      >
        <div className="flex grow flex-col overflow-hidden">
          <div className="border-b p-4">
            <Typography className="text-muted-foreground text-sm">
              Select a matter from the table below.
            </Typography>
          </div>
          <div className="flex min-h-0 flex-1 flex-col p-4">
            <div className="min-h-0 min-w-0 flex-1 overflow-auto">
              <MattersTable
                searchBar={
                  <MattersSearchBar
                    searchObject={searchObject}
                    addSearchObjectValue={addSearchObjectValue}
                    removeSearchObjectValue={removeSearchObjectValue}
                  />
                }
                tableName={tableName}
                searchObject={searchObject}
                onRowClick={(row) => {
                  onMatterSelected(row.original);
                  setOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      </Slideover>
    </div>
  );
};
